import React from "react";
import { Button, CircularProgress } from "@mui/joy";
import { Box } from "@mui/system";
import DarkInput from "../common/DarkInput";
import { useAuth } from "../../contexts/api";
import { useAlert } from "../../contexts/app/notificationToast";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  VisibilityIcon as Visibility,
  VisibilityOffIcon as VisibilityOff,
} from "../../styles/StyledMUI";
import FooterBrand from "../common/FooterBrand";
import IconButtonCTA from "../common/IconButtonCTA";
import { authCardDarkInputSx, colorTokens, withAlpha } from "../../styles/globalStyles";
import { useZekayAuthMark } from "../../utils/useZekayAuthMark";
import { userDisplayName } from "../../utils/userHelper";

export default function LoginPage() {
  const { src: zekayIconSrc, onImgError: onZekayIconError } = useZekayAuthMark();
  const [email, setEmail] = React.useState<string>("");
  const [password, setPassword] = React.useState<string>("");
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const { loginCallback, isLogged, user } = useAuth();
  const { showAlert } = useAlert();
  const [showPassword, setShowPassword] = React.useState<boolean>(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  React.useEffect(() => {
    const invited = searchParams.get("invited");
    if (invited !== "linked" && invited !== "created") {
      return;
    }

    const message =
      invited === "linked"
        ? "Votre compte a bien été associé à l’organisation. Connectez-vous pour accéder au portail."
        : "Votre accès est prêt. Connectez-vous avec votre e-mail et le mot de passe que vous venez de définir.";

    showAlert(message, "success");
    searchParams.delete("invited");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, showAlert]);

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleLogin = async () => {
    if (email && password) {
      setIsLoading(true);
      try {
        await loginCallback({ Email: email, Password: password });
      } catch (err) {
        if (err instanceof Error) showAlert(err.message, "danger");
      }
      setIsLoading(false);
    }
  };

  const handleSubmitForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await handleLogin();
  };

  React.useEffect(() => {
    if (isLogged && user) {
      showAlert("Bienvenue " + userDisplayName(user) + " !", "success");
      navigate("/");
    }
  }, [isLogged, navigate, showAlert, user]);

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        overflowX: "hidden",
        overflowY: "auto",
        padding: 2,
      }}
    >
      <Box
        sx={{
          flex: "1 1 auto",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 400,
            textAlign: "center",
            backgroundColor: "background.body",
            padding: 3,
            borderRadius: 2,
            boxShadow: `0 4px 12px ${withAlpha(colorTokens.bg.overlay, 0.1)}`,
          }}
        >
          {/* Logo */}
          <Box sx={{ display: "flex", justifyContent: "center", marginBottom: 3 }}>
            <img src={zekayIconSrc} width="84px" alt="Zekay icon" onError={onZekayIconError} />
          </Box>

          <Box
            component="form"
            onSubmit={handleSubmitForm}
            noValidate
            sx={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "stretch" }}
          >
            {/* Email Input */}
            <DarkInput
              placeholder="Email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value.toLowerCase())}
              sx={authCardDarkInputSx}
            />

            {/* Password Input */}
            <DarkInput
              placeholder="Mot de passe"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              endDecorator={
                <IconButtonCTA
                  type="button"
                  ctaVariant="neutral"
                  aria-label="Afficher/masquer mot de passe"
                  onClick={handleClickShowPassword}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButtonCTA>
              }
              sx={authCardDarkInputSx}
            />

            {/* Forgot password */}
            <Button
              type="button"
              variant="plain"
              size="sm"
              onClick={() => navigate("/request")}
              sx={{ fontSize: "0.8rem", marginY: 1 }}
            >
              Mot de passe oublié ?
            </Button>

            {/* Login button */}
            <Button
              type="submit"
              variant="solid"
              color="primary"
              sx={{ marginTop: 2, width: "100%" }}
              disabled={isLoading || !email || !password}
            >
              {isLoading ? <CircularProgress size="sm" /> : "Se connecter"}
            </Button>
          </Box>
        </Box>
      </Box>
      <FooterBrand showChromeNote />
    </Box>
  );
}
